import type { CoachJourneyInput } from '../schema';

/**
 * The WBBC onboarding as a conversation with Copilot. Simon's top posts and the ready-to-post
 * pictures land on a fresh board, then Copilot opens with the person's first message already
 * sent: Poppy interviews them (who they are, why they joined, why others should) one question
 * at a time and makes the carousel from their answers. The cards stay out of the way while
 * they talk, and the carousel is framed the moment it lands. For WBBC buyers; started by link
 * (`/new-onboarding?coach=wbbc-copilot`) or from Tutorials.
 */

/** The person's first message to Copilot, in their voice: what Poppy needs to run the interview. */
const OPENER =
  "Hey Poppy! 👋 I just joined Simon Squibb's World's Biggest Business Competition 🏆 Interview me with 3 quick questions, one at a time: who I am, why I joined, and why my friends should join too. Then turn my answers into a 5-slide carousel in the style of Simon's posts on this board, with a caption.";

type Transition = NonNullable<CoachJourneyInput['steps'][number]['on']>[number];

/** A carousel Copilot makes lands as a carousel node: that is the moment to show it. */
const CAROUSEL_MADE: Transition = {
  event: 'node.added',
  match: ['boardId'],
  if: { nodeType: 'carouselNode' },
  bindAs: { nodeId: 'carouselNodeId' },
  fresh: true,
  to: 'carousel',
};

const GROUP_GAP = 100;

export const wbbcCopilotJourney: CoachJourneyInput = {
  id: 'wbbc-copilot',
  version: 1,
  name: 'WBBC Challenge Post with Copilot',
  description: 'Chat with Poppy about why you joined the WBBC, and get a carousel to post.',
  mode: 'guided',
  dismissible: false,
  gate: { flag: 'wbbcCustomer' },
  start: { link: true },
  tutorial: {
    description: 'Chat with Poppy about why you joined, and get a carousel to post.',
    on: 'new-board',
  },
  steps: [
    {
      id: 'open',
      display: 'hidden',
      title: 'Open a board.',
      body: 'Open a board you can edit.',
      on: [{ event: 'board.entered', match: ['boardId'], bind: ['boardId'], to: 'layout' }],
    },
    {
      id: 'layout',
      display: 'hidden',
      title: 'Laying out the board.',
      body: 'Laying out the board.',
      do: [
        {
          action: 'board.add-group',
          params: { group: 'wbbc-top-posts', at: { x: 0, y: 0 } },
          bind: { groupNodeId: 'postsNodeId' },
        },
        {
          action: 'board.add-group',
          params: { group: 'wbbc-ready-pictures', at: { x: 1660 + GROUP_GAP, y: 0 }, hidden: true },
          bind: { groupNodeId: 'picturesNodeId' },
          then: 'posts',
        },
      ],
    },
    {
      id: 'posts',
      title: "Welcome to the WBBC! 🏆 Here's what's working for Simon",
      body: "These are Simon's 10 biggest posts right now.\n\nIn a minute you and I are going to turn YOUR story into a post just like these ✨",
      show: [{ node: 'postsNodeId', placement: 'right' }],
      focus: { node: 'postsNodeId', placement: 'right', padding: 0.15 },
      action: { label: 'Next Step', to: 'show-pictures' },
    },
    {
      id: 'show-pictures',
      display: 'hidden',
      title: 'Showing the pictures.',
      body: 'Showing the pictures.',
      do: { action: 'board.reveal', params: { nodes: ['picturesNodeId'] }, then: 'pictures' },
    },
    {
      id: 'pictures',
      title: 'Pictures ready to post 📸',
      body: 'Your carousel can use these too.\n\nNow the fun part: tell me your story and I’ll make the post 🎠',
      show: [{ node: 'picturesNodeId', placement: 'left' }],
      focus: { node: 'picturesNodeId', placement: 'left', padding: 0.15 },
      action: { label: "Let's chat 💬", to: 'start-chat' },
    },
    {
      id: 'start-chat',
      display: 'hidden',
      title: 'Opening Copilot.',
      body: 'Opening Copilot.',
      do: { action: 'copilot.ask', params: { prompt: OPENER }, then: 'thinking' },
    },
    {
      id: 'thinking',
      display: 'toast',
      title: 'Poppy’s on it ✨',
      body: 'Reading Simon’s posts and getting your first question ready.',
      resumeAt: 'your-turn',
      on: [CAROUSEL_MADE, { event: 'copilot.replied', match: ['boardId'], to: 'your-turn' }],
    },
    {
      id: 'your-turn',
      title: 'Your turn 💬',
      body: 'Answer Poppy right here, like you’d text a friend. The real version, not the polished one 😉\n\nShe’ll make your carousel once she has all 3 answers.',
      // Beside the box, with the conversation left in full view.
      show: [{ anchor: 'copilot.input', placement: 'left', spotlight: false }],
      on: [CAROUSEL_MADE, { event: 'copilot.sent', match: ['boardId'], to: 'thinking' }],
    },
    {
      id: 'carousel',
      title: 'Here’s your carousel! 🎠',
      body: 'Poppy is painting the slides now. They fill in over the next couple of minutes.\n\nYou can post it to social media right from here 🚀',
      show: [{ node: 'carouselNodeId', placement: 'bottom' }],
      focus: { node: 'carouselNodeId', padding: 0.3 },
      action: { label: 'Done! 🎉', to: 'finish' },
    },
    {
      id: 'finish',
      display: 'hidden',
      title: 'Done.',
      body: 'Done.',
      do: [
        { action: 'app.celebrate' },
        { action: 'app.survey', params: { survey: 'onboarding-tour' }, then: 'end' },
      ],
    },
  ],
};
